/**
 * Display formatting shared by the Collateral Monitoring pages (Health
 * Overview, Asset Classification, Valuation History, Concentration Risk) and
 * the dashboards that sit beside them.
 *
 * Before this module each page carried its own money formatter, so one book
 * printed as `$125K` on a tile, `125,000` in a donut center and `$125.00K` on
 * the next page. The rule now:
 *
 *   - a tile, a chart center or any other headline figure uses
 *     {@link formatUsdCompact} (`$125K`, `$4.2M`);
 *   - a table cell, which is a record an operator reads line by line, uses
 *     {@link formatUsd} (`$95,000.00`);
 *   - a percentage uses {@link formatPercent} at one decimal place.
 *
 * Raw enum and database codes never reach the screen as SCREAMING_CASE. They
 * pass through {@link labelFor}, which looks the code up in a label map and
 * humanizes any code the map does not carry. A value that is not an enum code
 * (a free-text name) is returned as it arrived.
 *
 * The currency and calendar-date formatters are the kit's own
 * (@aaimplatform/ui), re-exported here so these pages keep one import. This
 * module carried copies of them until the kit exported them from its root
 * (UI run r3, fi-cons-1 and fi-cons-3). The kit exports no percent formatter
 * yet, so {@link formatPercent} is still the single copy for these pages.
 */

import type { components } from '@aaimplatform/institutional-types/platform-admin';

export { formatUsd, formatUsdCompact, formatCalendarDate } from '@aaimplatform/ui';

type SecurityType = components['schemas']['SecurityType'];

/** A percentage at one decimal place: `80.0%`. */
export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

/**
 * Security.assetClass, declared as SecurityType in platform-admin.openapi.yaml
 * (EQUITY, FIXED_INCOME, ...), the instrument grouping the collateral
 * distribution reads group by.
 *
 * NOT the canonical 20-member AssetClass taxonomy (STABLECOINS,
 * PUBLIC_SECURITIES, ...), which keys every policy-bearing value. The two
 * enums share two members (CASH, OTHER) that mean different things in each,
 * so they never share a label map.
 *
 * SecurityType is registered as the `security-type` term in
 * .claude/manifests/terminology/terms/collateral.yaml, and this map is that
 * term's fiStaff context site: check-terminology.sh M2 reads its keys against
 * the enum, and the Record type makes tsc do the same. It stays hand-written
 * until admin-portal has a generated label module; a second module in this
 * repository is refused by the register's one-module-per-repo property, which
 * is an open operator question.
 */
export const SECURITY_ASSET_CLASS_LABELS: Readonly<Record<SecurityType, string>> = {
  EQUITY: 'Equity',
  FIXED_INCOME: 'Fixed income',
  MUTUAL_FUND: 'Mutual fund',
  ETF: 'ETF',
  OPTION: 'Option',
  FUTURE: 'Future',
  CASH: 'Cash',
  OTHER: 'Other',
};

/**
 * One color per Security.assetClass, keyed by the code, so an asset class
 * draws in the same color on every page that charts it. The Health Overview
 * donut took the kit's palette by position, so Equity and Fixed income swapped
 * colors between it and Asset Classification.
 *
 * Every entry is a data-viz or neutral token. The accent and error reds mark
 * CTAs and alerts only, so no asset class draws in either.
 */
export const SECURITY_ASSET_CLASS_COLORS: Readonly<Record<string, string>> = {
  EQUITY: 'rgb(var(--color-growth))',
  FIXED_INCOME: 'rgb(var(--color-alternative))',
  MUTUAL_FUND: 'rgb(var(--color-neutral-data))',
  ETF: 'rgb(var(--color-traditional))',
  OPTION: 'rgb(var(--color-tan-dark))',
  FUTURE: 'rgb(var(--color-text-secondary))',
  CASH: 'rgb(var(--color-success))',
  OTHER: 'rgb(var(--color-text-tertiary))',
};

/** The chart color for an asset class code; a code the map lacks draws as Other. */
export function securityAssetClassColor(code: string | null | undefined): string {
  const mapped = code != null ? SECURITY_ASSET_CLASS_COLORS[code] : undefined;
  return mapped ?? 'rgb(var(--color-text-tertiary))';
}

/** Valuation.valuationMethod (CollateralValuationMethod in loan-management.openapi.yaml). */
export const VALUATION_METHOD_LABELS: Readonly<Record<string, string>> = {
  MARK_TO_MARKET: 'Mark-to-market',
  LAST_TRADE: 'Last trade',
  MODEL: 'Model',
  MANUAL: 'Manual override',
};

/** RiskLimit.limitType and RiskLimit.limitLevel, the two halves of a bucket label. */
export const RISK_LIMIT_LABELS: Readonly<Record<string, string>> = {
  CONCENTRATION: 'Concentration',
  ASSET_CLASS: 'Asset class',
  ISSUER: 'Issuer',
  SINGLE_NAME: 'Single name',
};

/** Words kept upper case when a code is humanized. */
const ACRONYMS = new Set(['ETF', 'QA', 'LTV', 'UCC', 'USD', 'API', 'ID', 'EDD', 'FI']);

const ENUM_CODE = /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/;

/** `FIXED_INCOME` -> `Fixed income`; `SEED_QA_DEPTH` -> `Seed QA depth`. */
export function humanizeCode(code: string): string {
  const words = code.split('_').map((word) =>
    ACRONYMS.has(word) ? word : word.toLowerCase(),
  );
  const [first = '', ...rest] = words;
  const head = ACRONYMS.has(first) ? first : first.charAt(0).toUpperCase() + first.slice(1);
  return [head, ...rest].join(' ');
}

/**
 * The display label for a code: the map's entry, a humanized form of any other
 * SCREAMING_CASE code, or the value unchanged when it is not a code at all.
 */
export function labelFor(
  code: string | null | undefined,
  labels: Readonly<Record<string, string>> = {},
): string {
  if (code == null) return '';
  const mapped = labels[code];
  if (mapped) return mapped;
  return ENUM_CODE.test(code) ? humanizeCode(code) : code;
}
